import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Search, BookOpen, Sparkles, Users, DollarSign, Calendar,
  Camera, Package, FileText, BarChart3, Settings, HelpCircle,
  ChevronRight, Play, MessageSquare, Award, TrendingUp, Code
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleDownloadGuide = () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    window.open(`${API_URL}/api/docs/onboarding-guide`, '_blank');
  };

  const helpCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: BookOpen,
      color: 'from-blue-500 to-indigo-500',
      description: 'Complete guide for new clinic administrators',
      badge: 'Essential',
      topics: [
        { title: 'Download Complete Onboarding Guide', link: '#', action: handleDownloadGuide, isDownload: true },
        { title: 'Quick Start Video Tutorial', link: '/help/quick-start' },
        { title: 'First Steps Checklist', link: '/help/first-steps' },
        { title: 'Setting Up Your Profile', link: '/help/profile-setup' }
      ]
    },
    {
      id: 'ai-features',
      title: 'AI-Powered Features',
      icon: Sparkles,
      color: 'from-purple-500 to-pink-500',
      description: 'Learn about AI membership suggestions and revenue projections',
      badge: 'New',
      topics: [
        { title: 'AI Membership Suggestions', link: '/help/ai-suggestions' },
        { title: 'Revenue Projections Guide', link: '/help/revenue-projections' },
        { title: 'Business Intelligence Metrics', link: '/help/business-intelligence' },
        { title: 'Understanding Break-Even Analysis', link: '/help/break-even' }
      ]
    },
    {
      id: 'templates',
      title: 'Membership Templates',
      icon: FileText,
      color: 'from-teal-500 to-cyan-500',
      description: 'Browse, customize, and use pre-built membership templates',
      topics: [
        { title: 'Using Pre-Built Templates', link: '/help/templates' },
        { title: 'Creating Custom Templates', link: '/help/custom-templates' },
        { title: 'Template Categories Explained', link: '/help/template-categories' }
      ]
    },
    {
      id: 'plans',
      title: 'Membership Plans',
      icon: Award,
      color: 'from-blue-500 to-indigo-500',
      description: 'Create, manage, and optimize your membership offerings',
      topics: [
        { title: 'Creating Membership Plans', link: '/help/create-plans' },
        { title: 'Editing & Deleting Plans', link: '/help/manage-plans' },
        { title: 'Plan Pricing Strategies', link: '/help/pricing-strategies' },
        { title: 'Activating Plans', link: '/help/activate-plans' }
      ]
    },
    {
      id: 'aesthetic',
      title: 'Aesthetic Features',
      icon: Camera,
      color: 'from-pink-500 to-rose-500',
      description: 'Manage galleries, packages, and consultations',
      badge: 'Popular',
      topics: [
        { title: 'Before & After Gallery', link: '/help/gallery' },
        { title: 'Treatment Packages', link: '/help/packages' },
        { title: 'Consultation Bookings', link: '/help/consultations' },
        { title: 'Photo Management Best Practices', link: '/help/photo-management' }
      ]
    },
    {
      id: 'patients',
      title: 'Patient Management',
      icon: Users,
      color: 'from-green-500 to-emerald-500',
      description: 'Manage members, appointments, and communications',
      badge: 'Essential',
      topics: [
        { title: 'Walk-In Patient Registration Guide', link: '/help/walk-in-registration', featured: true },
        { title: 'Adding New Members', link: '/help/add-members' },
        { title: 'Appointment Scheduling', link: '/help/appointments' },
        { title: 'Messaging Patients', link: '/help/messaging' },
        { title: 'Member Dashboard Overview', link: '/help/member-dashboard' }
      ]
    },
    {
      id: 'billing',
      title: 'Billing & Payments',
      icon: DollarSign,
      color: 'from-yellow-500 to-orange-500',
      description: 'Handle payments, fees, and financial transactions',
      topics: [
        { title: 'Payment Processing', link: '/help/payments' },
        { title: 'Service Fees Setup', link: '/help/service-fees' },
        { title: 'Invoicing & Receipts', link: '/help/invoicing' },
        { title: 'Stripe Connect Integration', link: '/help/stripe' }
      ]
    },
    {
      id: 'analytics',
      title: 'Reports & Analytics',
      icon: BarChart3,
      color: 'from-cyan-500 to-blue-500',
      description: 'Track performance and generate insights',
      topics: [
        { title: 'Dashboard Analytics', link: '/help/analytics' },
        { title: 'Scheduled Reports', link: '/help/scheduled-reports' },
        { title: 'Revenue Tracking', link: '/help/revenue-tracking' },
        { title: 'Member Growth Metrics', link: '/help/growth-metrics' }
      ]
    },
    {
      id: 'widget-embed',
      title: 'Embed Widget',
      icon: Code,
      color: 'from-purple-500 to-violet-500',
      description: 'Embed your membership plans on any external website',
      badge: 'New',
      topics: [
        { title: 'Complete Widget Guide', link: '/help/widget' },
        { title: 'Add widget to WordPress', link: '/help/widget' },
        { title: 'Add widget to Wix', link: '/help/widget' },
        { title: 'Widget for Landing Pages', link: '/help/widget' },
        { title: 'View Widget Analytics', link: '/help/widget' }
      ]
    },
    {
      id: 'settings',
      title: 'Settings & Configuration',
      icon: Settings,
      color: 'from-gray-500 to-slate-500',
      description: 'Configure your practice settings and preferences',
      topics: [
        { title: 'Practice Profile Setup', link: '/help/practice-setup' },
        { title: 'User Management', link: '/help/user-management' },
        { title: 'Subscription Plans', link: '/help/subscription' },
        { title: 'Security & Privacy', link: '/help/security' }
      ]
    }
  ];

  const quickStartGuides = [
    {
      title: 'Walk-In Patient Registration',
      duration: '5 min',
      steps: 7,
      link: '/help/walk-in-registration',
      featured: true
    },
    {
      title: 'Getting Started with AI Suggestions',
      duration: '5 min',
      steps: 3,
      link: '/help/quick-start-ai'
    },
    {
      title: 'Create Your First Membership Plan',
      duration: '3 min',
      steps: 4,
      link: '/help/quick-start-plan'
    },
    {
      title: 'Set Up Your Practice Profile',
      duration: '8 min',
      steps: 6,
      link: '/help/quick-start-setup'
    }
  ];

  const popularTopics = [
    { title: 'Walk-In Patient Registration Guide', link: '/help/walk-in-registration', views: '2.1K' },
    { title: 'How to use AI Revenue Projections?', link: '/help/revenue-projections', views: '1.2K' },
    { title: 'Understanding Break-Even Analysis', link: '/help/break-even', views: '980' },
    { title: 'Saving AI-Generated Plans', link: '/help/save-ai-plans', views: '856' },
    { title: 'Upload Before & After Photos', link: '/help/upload-photos', views: '742' }
  ];

  const filteredCategories = helpCategories.filter(category =>
    searchQuery === '' ||
    category.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.topics.some(topic => topic.title.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-teal-600 hover:text-teal-700">
              <ChevronRight className="h-5 w-5 rotate-180" />
              <span className="font-semibold">Back to Home</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-gray-600 hover:text-gray-900 font-medium">
                Dashboard
              </Link>
              <Button asChild variant="outline">
                <Link to="/admin">Go to App</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center gap-3">
            <div className="h-12 w-12 rounded-full bg-teal-100 flex items-center justify-center">
              <HelpCircle className="h-6 w-6 text-teal-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900">Help Center</h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Find answers, learn features, and get the most out of MyMedMember
          </p>
        </div>

        {/* Search Bar */}
        <Card className="max-w-3xl mx-auto">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search for help topics, features, or questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 text-base"
              />
            </div>
          </CardContent>
        </Card>

        {/* Quick Start Guides */}
        {searchQuery === '' && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Play className="h-5 w-5 text-teal-600" />
              <h2 className="text-2xl font-bold text-gray-900">Quick Start Guides</h2>
            </div>
            <div className="grid md:grid-cols-4 gap-4">
              {quickStartGuides.map((guide, index) => (
                <Card 
                  key={index} 
                  className={`hover:shadow-lg transition-shadow cursor-pointer ${guide.featured ? 'border-2 border-teal-500 bg-gradient-to-br from-teal-50 to-cyan-50' : ''}`} 
                  onClick={() => navigate(guide.link)}
                >
                  <CardHeader>
                    {guide.featured && (
                      <Badge className="w-fit mb-2 bg-teal-600 text-white border-0 text-xs">
                        Staff Essential
                      </Badge>
                    )}
                    <CardTitle className="text-base">{guide.title}</CardTitle>
                    <div className="flex gap-3 mt-2">
                      <Badge variant="outline" className="text-xs">{guide.duration}</Badge>
                      <Badge variant="outline" className="text-xs">{guide.steps} steps</Badge>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Help Categories */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="h-5 w-5 text-teal-600" />
            <h2 className="text-2xl font-bold text-gray-900">Browse by Category</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {filteredCategories.map((category) => {
              const Icon = category.icon;
              return (
                <Card key={category.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-lg bg-gradient-to-br ${category.color} flex items-center justify-center`}>
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{category.title}</CardTitle>
                          {category.badge && (
                            <Badge className="mt-1 bg-teal-600 text-white border-0 text-xs">
                              {category.badge}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <CardDescription className="mt-2">{category.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {category.topics.map((topic, idx) => (
                        topic.isDownload ? (
                          <button
                            key={idx}
                            onClick={topic.action}
                            className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors group w-full text-left"
                          >
                            <span className="text-sm text-gray-700 group-hover:text-teal-600 font-medium">
                              📥 {topic.title}
                            </span>
                            <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-teal-600" />
                          </button>
                        ) : (
                          <Link
                            key={idx}
                            to={topic.link}
                            className={`flex items-center justify-between p-2 rounded-lg transition-colors group ${topic.featured ? 'bg-teal-50 hover:bg-teal-100 border border-teal-200' : 'hover:bg-gray-50'}`}
                          >
                            <span className={`text-sm group-hover:text-teal-600 ${topic.featured ? 'text-teal-700 font-medium' : 'text-gray-700'}`}>
                              {topic.featured && '⭐ '}{topic.title}
                            </span>
                            <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-teal-600" />
                          </Link>
                        )
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Popular Topics */}
        {searchQuery === '' && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-5 w-5 text-teal-600" />
              <h2 className="text-2xl font-bold text-gray-900">Popular Topics</h2>
            </div>
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  {popularTopics.map((topic, index) => (
                    <Link
                      key={index}
                      to={topic.link}
                      className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center text-sm font-semibold text-teal-600">
                          {index + 1}
                        </div>
                        <span className="text-sm font-medium text-gray-700 group-hover:text-teal-600">
                          {topic.title}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">{topic.views} views</span>
                        <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-teal-600" />
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Still Need Help */}
        <Card className="bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <MessageSquare className="h-8 w-8 text-teal-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">Still need help?</h3>
                  <p className="text-sm text-gray-600">Our support team is here to assist you</p>
                </div>
              </div>
              <Button 
                className="bg-teal-600 hover:bg-teal-700"
                onClick={() => window.location.href = 'mailto:support@mymedmember.com?subject=Help%20Center%20Support%20Request'}
              >
                Contact Support
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HelpCenter;
